/**
 * HeuteWidget — embeddable "Bodenblick Heute" card showing the latest
 * published daily post (image + title + teaser). Updates daily.
 *
 * Route: /widget/heute
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Newspaper, ArrowRight } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function HeuteWidget() {
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/daily/today`)
      .then((r) => setPost((r.data?.posts || [])[0] || null))
      .catch(() => setError(true));
  }, []);

  if (error || (post === null && !error)) {
    // both loading + error use same skeleton; cheaper UI
  }

  return (
    <>
      <Helmet>
        <title>Bodenblick · Heute</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div
        className="min-h-screen bg-[#121212] p-4 flex items-center justify-center"
        data-testid="widget-heute"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
      >
        <a
          href={post ? `https://bodenblick.com/heute#${post.id}` : "https://bodenblick.com/heute"}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full max-w-[360px] border border-white/15 bg-[#121212] hover:shadow-[4px_4px_0_0_#1A1A1A] transition-shadow"
          data-testid="widget-heute-link"
        >
          <div className="flex items-center justify-between border-b border-white/15 px-4 py-2 bg-[#00FA9A] text-black">
            <span className="font-bold text-xs uppercase tracking-[0.18em] flex items-center gap-1.5">
              <Newspaper size={12} weight="fill" /> Bodenblick · Heute
            </span>
            <span className="text-[9px] font-mono opacity-80">{(post?.date_iso || "").slice(5)}</span>
          </div>
          {post?.image_url && (
            <div className="aspect-[16/9] bg-white/10 overflow-hidden">
              <img
                src={`${API}${post.image_url}`}
                alt={post.title || ""}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-4">
            {post ? (
              <>
                <div className="text-[9px] uppercase tracking-widest text-[#00FA9A] font-mono">
                  {post.topic_label || "Bodenblick"}
                </div>
                <h3 className="font-bold text-base text-white leading-tight mt-1 line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-[12px] text-white/70 leading-snug mt-1 line-clamp-3">
                  {post.teaser}
                </p>
              </>
            ) : error ? (
              <div className="text-sm text-white/60">Heute kein Beitrag verfügbar.</div>
            ) : (
              <div className="animate-pulse space-y-2">
                <div className="h-3 bg-white/10 w-1/4" />
                <div className="h-4 bg-white/10 w-3/4" />
                <div className="h-3 bg-white/10 w-full" />
              </div>
            )}
          </div>
          <div className="border-t border-white/15 px-4 py-2 bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-[#00FA9A] font-mono flex items-center justify-between">
            <span>bodenblick.com</span>
            <ArrowRight size={11} />
          </div>
        </a>
      </div>
    </>
  );
}
